import "./App.css";
import * as THREE from "three";
import Viewport from "./Viewport";
import { ContactShadows, OrbitControls, OrthographicCamera, PerspectiveCamera } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useEffect, useReducer, useRef, useState } from "react";
import { COMPONENT_TYPES, DEFAULT_EMPTY_THICKNESS, DIRECTION_EXTRACT, DIRECTION_SUPPLY, COMPONENT_ALIGNMENT, TOOL_MODE } from "./Constants";
import Unit from "./Components/Unit";
import { ChakraProvider, extendTheme, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import Header from "./Header";
import Inspector from "./Tools/Inspector";
import Toolbar from "./Tools/Toolbar";
import frameReducer from "./Store/frameReducer";
import { fromMM } from "./Utils/frameUtils";
import { useStore } from "./Store/zustandStore";
import Dimensions from "./Components/Dimensions";
import Drawings from "./Drawings";

const theme = {
  styles: {
    global: {
      "html, body": {
        fontFamily: "Inter",
      },
    },
  },
};

const customTheme = extendTheme(theme);

function App() {
  const [setSelectedUUID, toolMode, showDimensions, frameThickness] = useStore((state) => [state.setSelectedUUID, state.toolMode, state.showDimensions, state.frameThickness]);

  const [frames, dispatch] = useReducer(frameReducer, example);
  const [isOrthographic, setIsOrthographic] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cursor, setCursor] = useState("move");
  const [pickup, setPickup] = useState();
  const [draggingEnabled, setDraggingEnabled] = useState(false);
  const controlsRef = useRef(null);

  useEffect(() => {
    if (controlsRef.current) controlsRef.current.autoRotate = autoRotate;
  }, [autoRotate]);

  useEffect(() => {
    setCursor(toolMode == TOOL_MODE.MOVE ? "move" : "default");
  }, [toolMode]);

  function resetControls() {
    controlsRef.current.position0.set(0, 0.25, 8);
    controlsRef.current.target0.set(0, 0.25, 0);
    controlsRef.current.reset();
  }

  function updateUnit(type, id, value) {
    dispatch({ type: type, id: id, value: value });
  }

  const addFrame = (component, position, length, height, width, placing) => {
    const isHeatExchanger = component === COMPONENT_TYPES.PLATE_HEAT_EXCHANGER || component === COMPONENT_TYPES.ROTARY_HEAT_EXCHANGER;
    const isFilter = component === COMPONENT_TYPES.FILTER;

    const newLength = length || (isHeatExchanger ? 750 : 500);
    const newHeight = height || (isHeatExchanger ? 1000 : 500);
    const newWidth = width || 500;

    let newPosition = [0, fromMM(newHeight / 2), 0];

    if (frames.length > 0) {
      const furthestObject = frames.reduce((prev, current) => (prev.position[0] > current.position[0] ? prev : current));
      newPosition = furthestObject.position;
      newPosition[0] += fromMM(furthestObject.length * 1.5);
      newPosition[1] = fromMM(newHeight / 2);
    }

    updateUnit(
      "FRAMES",
      -1,
      frames.concat({
        id: THREE.MathUtils.generateUUID(),
        position: position || newPosition,
        length: newLength,
        height: newHeight,
        width: newWidth,
        connections: connections[component === COMPONENT_TYPES.ROTARY_HEAT_EXCHANGER ? 2 : component === COMPONENT_TYPES.PLATE_HEAT_EXCHANGER ? 1 : 0].map((c) => {
          return { ...c, id: THREE.MathUtils.generateUUID() };
        }),
        hasLeftFrame: !isFilter,
        hasRightFrame: !isFilter,
        hasFrame: true,
        hasFloor: true,
        hasCeiling: true,
        hasLeftWall: false,
        hasRightWall: false,
        component: component,
        //placing: placing == undefined || placing,
      })
    );
  };

  return (
    <ChakraProvider theme={customTheme}>
      <Header
        isOrthographic={isOrthographic}
        setIsOrthographic={setIsOrthographic}
        autoRotate={autoRotate}
        setAutoRotate={setAutoRotate}
        resetControls={resetControls}
        draggingEnabled={draggingEnabled}
        setDraggingEnabled={setDraggingEnabled}
        frames={frames}
        updateUnit={updateUnit}
      />

      <Tabs variant="unstyled" size="md">
        <TabList borderBottom={"solid 1px #dedede"} backgroundColor={"white"}>
          <Tab fontSize={12} _selected={{ fontWeight: "bold" }}>
            Designer
          </Tab>
          <Tab fontSize={12} _selected={{ fontWeight: "bold" }}>
            Drawing
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel padding={0}>
            <div style={{ display: "flex", cursor: cursor }}>
              <Viewport>
                <Canvas
                  flat
                  dpr={[1, 2]}
                  onPointerMissed={() => {
                    if (toolMode == TOOL_MODE.SELECTION) setSelectedUUID(null);
                  }}
                >
                  <PerspectiveCamera position={[0, 0.25, 8]} fov={25} makeDefault={!isOrthographic} />
                  <OrthographicCamera position={[0, 0, 8]} zoom={200} makeDefault={isOrthographic} />
                  <ambientLight />
                  <pointLight position={[10, 20, 10]} />
                  <Dimensions enabled={showDimensions} frames={frames}></Dimensions>
                  <Unit
                    frames={frames}
                    draggingEnabled={draggingEnabled}
                    setPickup={setPickup}
                    pickup={pickup}
                    isDragging={isDragging}
                    setIsDragging={setIsDragging}
                    updateUnit={updateUnit}
                    addFrame={addFrame}
                  />
                  <ContactShadows rotation-x={Math.PI / 2} position={[0, 0, 0]} opacity={0.5} length={10} height={10} blur={0.2} far={0.8} />
                  <OrbitControls attach={"orbitControls"} makeDefault ref={controlsRef} enabled={!isDragging} target={[0, 0.25, 0]} />
                </Canvas>
              </Viewport>
              <Inspector frames={frames} addFrame={addFrame} updateUnit={updateUnit} />
            </div>
          </TabPanel>
          <TabPanel>
            <Drawings />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </ChakraProvider>
  );
}

const connections = [
  [
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [1],
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [1],
    },
  ],
  [
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [1, 0],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [0, 1],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [1, 0],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [0, 1],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
  ],
  [
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [1, 0],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [0, 1],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [1, 0],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [0, 1],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
  ],
];

const defaultData = [
  {
    id: THREE.MathUtils.generateUUID(),
    position: [-0.25 - fromMM(DEFAULT_EMPTY_THICKNESS), 0.25 + fromMM(DEFAULT_EMPTY_THICKNESS), 0],
    length: 500,
    height: 500,
    width: 500,
    connections: connections[0].map((c) => {
      return { ...c, id: THREE.MathUtils.generateUUID() };
    }),
    hasLeftFrame: true,
    hasRightFrame: true,
    hasFrame: true,
    hasFloor: true,
    hasCeiling: true,
    hasLeftWall: false,
    hasRightWall: false,
    component: COMPONENT_TYPES.EMPTY,
  },
  {
    id: THREE.MathUtils.generateUUID(),
    position: [0.25 + fromMM(DEFAULT_EMPTY_THICKNESS), 0.25 + fromMM(DEFAULT_EMPTY_THICKNESS), 0],
    length: 500,
    height: 500,
    width: 500,
    connections: connections[0].map((c) => {
      return { ...c, id: THREE.MathUtils.generateUUID() };
    }),
    hasLeftFrame: true,
    hasRightFrame: true,
    hasFrame: true,
    hasFloor: true,
    hasCeiling: true,
    hasLeftWall: false,
    hasRightWall: false,
    component: COMPONENT_TYPES.EMPTY,
  },
];

const example = JSON.parse(
  '[{"id":"24230d7b-7d4e-4864-8e55-56b899ef4c10","position":[1.6234134614320368,0.5350000262260437,0.08971674248007222],"length":630,"height":1000,"width":1000,"connections":[{"side":-1,"position":[1],"id":"f1690351-9d1e-4a42-8d75-18be5812e0d0","attachedTo":"68156041-fb6d-4709-8dcc-3a4e918c440f","direction":null},{"side":1,"position":[1],"id":"16133ce5-57fb-4b9e-83f6-d77aeccbc812","attachedTo":"eef877fc-8399-4239-a126-1294cec9c34a","direction":null}],"hasControlBox":false,"hasLeftFrame":false,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":12,"componentAlignment":-1,"uv":{"tubeWidth":200,"requiredLights":2},"door":{"visible":true}},{"id":"997eee24-df39-46c6-bb90-449fa74f038d","position":[-0.2665865385679632,0.5350000262260437,0.08971674248007222],"length":250,"height":1000,"width":1000,"connections":[{"side":-1,"position":[1],"id":"870717d6-64b9-4fe9-bedf-efc00daf90af","attachedTo":"b09ca2e4-5502-4077-9159-241bb9f505b1","direction":null},{"side":1,"position":[1],"id":"a728b63c-79b4-4dae-829c-30b36064192d","attachedTo":"5f2173a3-2163-49ff-b685-e8e7f7181963","direction":null}],"hasLeftFrame":false,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":1,"door":{"visible":true}},{"id":"3dae8bfa-167f-4a98-aa8a-7ede15bb3bcd","position":[0.2084134614320367,0.5350000262260437,0.08971674248007222],"length":630,"height":1000,"width":1000,"connections":[{"id":"5f2173a3-2163-49ff-b685-e8e7f7181963","side":-1,"position":[1],"attachedTo":"a728b63c-79b4-4dae-829c-30b36064192d","direction":null},{"id":"1d5f630a-fcff-4197-889c-6ed9f468c134","side":1,"position":[1],"attachedTo":"8b80802c-fd6c-4802-b97b-d693a9936b45","direction":null}],"hasLeftFrame":false,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":9,"carbonFilter":{"requiredFilters":1},"door":{"visible":true}},{"id":"8cedb786-50ea-4b1b-8feb-c03ae28e5450","position":[0.6834134614320366,0.5350000262260437,0.08971674248007222],"length":250,"height":1000,"width":1000,"connections":[{"id":"8b80802c-fd6c-4802-b97b-d693a9936b45","side":-1,"position":[1],"attachedTo":"1d5f630a-fcff-4197-889c-6ed9f468c134","direction":null},{"id":"4f5ae8fe-88cb-4f7f-ae3d-8636f2f3cdb0","side":1,"position":[1],"attachedTo":"fe689e23-602b-4329-942c-0140b9cf1db5","direction":null}],"hasLeftFrame":false,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":1,"filter":{"grade":"M5"},"door":{"visible":true}},{"id":"19c2c70b-4937-4351-90f6-18529e9a754c","position":[3.048413461432038,0.5350000262260437,0.08971674248007222],"length":750,"height":1000,"width":1000,"connections":[{"id":"218e6c0f-c61f-4fa0-8bf1-2769da1429a4","side":-1,"position":[1],"attachedTo":"d0795236-18ce-4070-ac91-e1b0fdf80bbf","direction":null},{"id":"bcf64593-7651-4d8c-a854-0c6bb371193e","side":1,"position":[1],"attachedTo":null,"direction":null}],"hasLeftFrame":false,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":3,"hasFlange":true,"flangeAlignment":1,"door":{"visible":true}},{"id":"91aed13b-bda7-48d3-9b6c-bfe61ff949ba","position":[1.0584134614320369,0.5350000262260437,0.08971674248007222],"length":430,"height":1000,"width":1000,"connections":[{"id":"fe689e23-602b-4329-942c-0140b9cf1db5","side":-1,"position":[1],"attachedTo":"4f5ae8fe-88cb-4f7f-ae3d-8636f2f3cdb0","direction":null},{"id":"68156041-fb6d-4709-8dcc-3a4e918c440f","side":1,"position":[1],"attachedTo":"f1690351-9d1e-4a42-8d75-18be5812e0d0","direction":null}],"hasLeftFrame":false,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":8,"componentAlignment":-1,"door":{"visible":true}},{"id":"6b34c09e-3026-4a8f-b039-6fa97749749a","position":[-1.341586538567964,0.5350000262260437,0.08971674248007222],"length":500,"height":1000,"width":1000,"connections":[{"id":"d0ae655b-6f35-41c7-bbe5-0af5fb7d13cc","side":-1,"position":[1],"attachedTo":null,"direction":null},{"id":"e85764cd-7ee0-423e-a15f-8442bbdce521","side":1,"position":[1],"attachedTo":"695e7537-6f62-4155-8c3a-cb04d2b5e3b8","direction":null}],"hasLeftFrame":true,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":-1,"door":{"visible":true}},{"id":"ceb0cd35-07d9-43fc-bf55-fc56fd1f1455","position":[-0.7415865385679635,0.5350000262260437,0.08971674248007222],"length":630,"height":1000,"width":1000,"connections":[{"id":"695e7537-6f62-4155-8c3a-cb04d2b5e3b8","side":-1,"position":[1],"attachedTo":"e85764cd-7ee0-423e-a15f-8442bbdce521","direction":null},{"id":"b09ca2e4-5502-4077-9159-241bb9f505b1","side":1,"position":[1],"attachedTo":"870717d6-64b9-4fe9-bedf-efc00daf90af","direction":null}],"hasLeftFrame":false,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":10,"componentAlignment":-1,"bagFilter":{"filterThickness":80,"gap":50,"twoRows":true,"filterLength":150},"door":{"visible":true}},{"id":"e3bc0cd0-b103-4163-b828-498fd0b79e04","position":[2.3234134614320374,0.5350000262260437,0.08971674248007222],"length":630,"height":1000,"width":1000,"connections":[{"id":"eef877fc-8399-4239-a126-1294cec9c34a","side":-1,"position":[1],"attachedTo":"16133ce5-57fb-4b9e-83f6-d77aeccbc812","direction":null},{"id":"d0795236-18ce-4070-ac91-e1b0fdf80bbf","side":1,"position":[1],"attachedTo":"218e6c0f-c61f-4fa0-8bf1-2769da1429a4","direction":null}],"hasLeftFrame":true,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":11,"door":{"visible":true}}]'
);

export default App;
